/**
 * ตรรกะล้วนของหน้าจอ MRP Run (P2-6e, D-100/D-101) แยกออกจาก JSX เพื่อให้ทดสอบกฎได้
 * ตรง ๆ — แนวเดียวกับ `lib/forecasts/forecastsView.ts`
 *
 * สี่ข้อที่ผิดแล้วแพงที่สุดของกลุ่มนี้:
 *
 * 1. **`projected_available_qty` ต้องถูกติดป้ายว่าเป็นยอดที่เข้า ไม่ใช่ยอดที่ออก**
 *    (`PROJECTED_AVAILABLE_LABEL`) — `docs/API_CONTRACT.md` เขียนไว้ตรง ๆ และเป็น
 *    ฟิลด์ที่คนอ่านตารางเร็ว ๆ ตีความผิดทางได้ง่ายที่สุด
 * 2. **รันไหนปล่อยได้** (`canReleaseMrpRun`) ต้องอ่านสถานะออกมาเป็นคำตอบเดียว ไม่ใช่
 *    เงื่อนไข `if` ที่กระจายอยู่ใน JSX — และเป็นที่เดียวที่บอกว่า `SUPERSEDED` ไม่มี
 *    ปุ่มปล่อยเลย
 * 3. **การประกอบ body ของการเริ่มรัน** (`toCreateMrpRunBody`) ซึ่งเป็นด่านเดียวที่
 *    ทางเขียนของหน้าจอนี้เดินผ่าน
 * 4. **ไม่มีฟังก์ชันไหนคำนวณ/ปัด/รวมตัวเลขของ planned order เลย** (D-095/D-099/
 *    D-100) — ทุกจำนวนที่ไฟล์นี้แตะเป็นสตริงที่ส่งต่อหรือกรองเท่านั้น ไม่เคยผ่าน
 *    `Number(...)` หรือคำนวณซ้ำ
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่ประกอบ `status`, `run_id` หรือฟิลด์ผู้กระทำเลย** —
 * ทั้งหมดเป็นของเซิร์ฟเวอร์ (D-100)
 */

import {
  ACTIVE_PULL_POLICY_EXCLUSION_REASON,
  BUY_ORDER_TYPE,
  CURRENT_MRP_RUN_STATUS,
  LOT_FOR_LOT_PROCEDURE,
  MAKE_ORDER_TYPE,
  MOQ_PROCEDURE,
  PRODUCTION_ORDER_DOCUMENT_TYPE,
  PURCHASE_REQUISITION_DOCUMENT_TYPE,
  SUPERSEDED_MRP_RUN_STATUS,
  type CreateMrpRunBody,
  type MrpRunSummary,
} from './types';

/* ------------------------------------------------------------- สถานะ ----- */

export interface MrpRunStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * สองสถานะตามลำดับ lifecycle (D-100) — ข้อความต้องบอกว่า **ปล่อยได้ไหม** เพราะนั่น
 * คือคำถามเดียวที่ผู้วางแผนถามเมื่อเห็นป้ายนี้
 */
export const MRP_RUN_STATUS_DESCRIPTORS: readonly MrpRunStatusDescriptor[] = [
  {
    code: CURRENT_MRP_RUN_STATUS,
    label: 'ใช้งานอยู่ (CURRENT)',
    summary: 'แผนล่าสุดของคู่ (โรงงาน, MPS) นี้ — **ปล่อยเป็นเอกสารจริงได้**',
  },
  {
    code: SUPERSEDED_MRP_RUN_STATUS,
    label: 'ถูกแทนด้วยรันใหม่ (SUPERSEDED)',
    summary:
      'มีการรันใหม่ของคู่เดียวกันนี้เกิดขึ้นแล้ว — **รันนี้ยังอยู่และยังเปิดอ่านได้ แต่ปล่อยไม่ได้อีกแล้ว** เพราะเป็นเลขที่ถูกแทนที่ไปแล้ว',
  },
];

export type MrpRunStatusTone = 'neutral' | 'warning' | 'success' | 'info' | 'danger';

export function mrpRunStatusTone(status: string): MrpRunStatusTone {
  switch (status) {
    case CURRENT_MRP_RUN_STATUS:
      return 'success';
    case SUPERSEDED_MRP_RUN_STATUS:
      return 'info';
    default:
      return 'neutral';
  }
}

/** ชื่อที่อ่านออกของสถานะ — คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้นหรือคืนช่องว่าง */
export function mrpRunStatusLabel(status: string): string {
  return MRP_RUN_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status;
}

/* ------------------------------------------------------------ lifecycle -- */

/**
 * รันนี้ปล่อยได้หรือไม่ — **`CURRENT` เท่านั้น ที่เหลือไม่มีเลย**
 *
 * `SUPERSEDED` ไม่มีปุ่มเพราะเซิร์ฟเวอร์จะตอบ `422 MRP_RUN_NOT_CURRENT` แน่นอน
 * (D-100 — รันที่ถูกแทนแล้วคือแผนที่ถูกแก้ไปแล้ว ซื้อ/สร้างตามเลขนั้นไม่ได้อีก)
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตรวจสถานะของทุกคำขอเสมอ
 */
export function canReleaseMrpRun(status: string): boolean {
  return status === CURRENT_MRP_RUN_STATUS;
}

/** สิทธิ์ที่ทั้งปุ่ม "เริ่มรัน" และปุ่ม "ปล่อย" ต้องการ — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์
 * (D-101: การปล่อยไม่ต้องการ APPROVE เพิ่ม เพราะทุกเอกสารที่มันสร้างเป็น `DRAFT`
 * เสมอ และคนที่รันเลขคือคนที่รู้ว่าเลขถูก — คู่ตาที่สองอยู่ที่ตัวเอกสารแทน) */
export const MRP_RUN_ACTION = 'CREATE' as const;

/* --------------------------------------------------- ชนิดของ order/เอกสาร -- */

export function mrpOrderTypeLabel(orderType: string): string {
  switch (orderType) {
    case MAKE_ORDER_TYPE:
      return 'ผลิตเอง (MAKE)';
    case BUY_ORDER_TYPE:
      return 'ต้องซื้อ (BUY)';
    default:
      return orderType;
  }
}

export function mrpLotSizeProcedureLabel(procedure: string): string {
  switch (procedure) {
    case LOT_FOR_LOT_PROCEDURE:
      return 'สั่งเท่าที่ขาด (LOT_FOR_LOT)';
    case MOQ_PROCEDURE:
      return 'ปริมาณสั่งขั้นต่ำ (MOQ)';
    default:
      return procedure;
  }
}

export function mrpExclusionReasonLabel(reason: string): string {
  switch (reason) {
    case ACTIVE_PULL_POLICY_EXCLUSION_REASON:
      return 'อยู่ใต้นโยบาย Pull ที่เปิดใช้งาน — เติมด้วย Kanban ไม่ใช่ MRP';
    default:
      return reason;
  }
}

export function mrpDocumentTypeLabel(documentType: string): string {
  switch (documentType) {
    case PRODUCTION_ORDER_DOCUMENT_TYPE:
      return 'ใบสั่งผลิต (Production Order)';
    case PURCHASE_REQUISITION_DOCUMENT_TYPE:
      return 'ใบขอซื้อ (Purchase Requisition)';
    default:
      return documentType;
  }
}

/**
 * ป้ายของคอลัมน์ `projected_available_qty` — **ยอดที่เข้า bucket นี้ ไม่ใช่ที่ออก**
 *
 * `docs/API_CONTRACT.md` เขียนไว้ตรง ๆ ว่าเป็นยอดคงเหลือ**ก่อน**หักความต้องการของ
 * bucket นั้น (ยอด**เข้า**) — ไม่ใช่ยอดที่เหลือ**หลัง**หักแล้ว (ยอด**ออก**) ซึ่งเป็น
 * ตัวเลขที่คอลัมน์นี้ไม่ได้ถือ ป้ายจึงต้องพูดถึง "ก่อนตัด" ตรง ๆ เพื่อไม่ให้คนอ่านตาราง
 * เร็ว ๆ ตีความสลับทาง
 */
export const PROJECTED_AVAILABLE_LABEL = 'คงเหลือก่อนตัด (ยอดเข้า bucket)';

/** คำอธิบายยาวของคอลัมน์เดียวกัน — ใช้เป็น hint ใต้หัวตาราง */
export const PROJECTED_AVAILABLE_HINT =
  'ยอดคงเหลือ ณ ตอน**เข้า**ช่วงความต้องการของวันนี้ (หลังหัก safety stock แล้ว) — ไม่ใช่ยอดที่เหลือหลังตัดความต้องการของวันนี้ออกไปแล้ว';

/* ------------------------------------------------------- ค้นหาและตัวกรอง -- */

/**
 * กรองรายการด้วยคำค้นเดียว — เทียบ **ชื่อ MPS และรหัสโรงงาน**
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/mrp-runs` ไม่มีพารามิเตอร์ค้นหาอิสระ มีแต่ตัวกรองแบบตรงตัว
 * (`plant_code`/`status`) ส่วนตัวกรองสถานะบนหน้าจอใช้ของจริงเหล่านั้น
 */
export function filterMrpRuns(
  items: readonly MrpRunSummary[],
  query: string,
): readonly MrpRunSummary[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (run) =>
      run.mps_version_label.toLocaleLowerCase().includes(needle) ||
      run.plant_code.toLocaleLowerCase().includes(needle),
  );
}

/** ค่าที่ตัวกรองสถานะส่งให้ `GET /v1/mrp-runs?status=` — คืน `undefined` เมื่อ
 * "ทุกสถานะ" (คีย์ที่ไม่ได้ระบุต้องไม่เดินทางไปเลย) */
export function toMrpRunStatusFilter(status: string): string | undefined {
  const trimmed = status.trim();
  return trimmed === '' ? undefined : trimmed;
}

/* ------------------------------------------------------- ค่าที่ฟอร์มกรอก -- */

/** ค่าที่ฟอร์ม "เริ่มรัน MRP" ถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ */
export interface CreateMrpRunFields {
  readonly plantCode: string;
  readonly mpsVersionLabel: string;
}

export const EMPTY_CREATE_MRP_RUN_FIELDS: CreateMrpRunFields = {
  plantCode: '',
  mpsVersionLabel: '',
};

/**
 * ประกอบ body ของ `POST /v1/mrp-runs` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * สองช่องเท่านั้นและทั้งคู่บังคับ — ตรงกับ `MrpRunCreateRequest` ที่มีสองฟิลด์พอดี
 * ไม่มีการตรวจว่า MPS ชื่อนี้มีจริงหรือ `RELEASED` หรือยัง: นั่นเป็นกฎของเซิร์ฟเวอร์
 * (`MPS_NOT_FOUND`/`MPS_NOT_RELEASED`) และหน้าจอนี้มีอยู่เพื่อ **แสดง** ผลของกฎนั้น
 */
export function toCreateMrpRunBody(fields: CreateMrpRunFields): CreateMrpRunBody | null {
  const plantCode = fields.plantCode.trim();
  const mpsVersionLabel = fields.mpsVersionLabel.trim();

  if (plantCode === '' || mpsVersionLabel === '') {
    return null;
  }

  return { plant_code: plantCode, mps_version_label: mpsVersionLabel };
}

/**
 * วันที่ของบรรทัดในรูปที่แสดงบนหน้าจอ — **ค่าเดิมทุกตัวอักษร**
 *
 * ไม่มีการแปลงใด ๆ เกิดขึ้นที่นี่โดยเจตนา (แนวเดียวกับ `forecastDateLabel()`):
 * `required_date` เป็น `DATE` ฝั่งเซิร์ฟเวอร์และห้ามเดินผ่าน `Date` เพราะ offset ของ
 * เครื่องผู้ใช้จะเลื่อนวันได้
 */
export function mrpRequiredDateLabel(value: string): string {
  return value;
}
